import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  CircularProgress,
  Avatar,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

/** Metric Card Component */
export const MetricCard = ({
  title,
  value,
  fullValue,
  subtitle,
  trend,
  action,
  progress,
  color,
  loading,
}) => (
  <Card
    sx={{
      borderRadius: 3,
      height: "100%",
      position: "relative",
      overflow: "visible",
    }}
  >
    <CardContent>
      {loading ? (
        <Box
          sx={{
            minHeight: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                color: "#6b7280",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {title}
            </Typography>
            {trend &&
              (trend === "up" ? (
                <TrendingUpIcon sx={{ color: "#10b981", fontSize: 20 }} />
              ) : (
                <TrendingDownIcon sx={{ color: "#dc2626", fontSize: 20 }} />
              ))}
          </Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 700, color: color || "#1a1a1a", mb: 0.5 }}
          >
            {value}
          </Typography>
          {fullValue && (
            <Typography
              variant="caption"
              sx={{
                color: "#9ca3af",
                display: "block",
                mb: 1,
              }}
            >
              {fullValue}
            </Typography>
          )}
          {subtitle && (
            <Typography
              variant="body2"
              sx={{ color: "#6b7280", fontSize: "0.875rem" }}
            >
              {subtitle}
            </Typography>
          )}
          {progress !== undefined && (
            <LinearProgress
              variant="determinate"
              value={Math.min(progress, 100)}
              sx={{
                mt: 1,
                height: 6,
                borderRadius: 3,
                bgcolor: "#e5e7eb",
                "& .MuiLinearProgress-bar": {
                  bgcolor:
                    progress > 80
                      ? "#dc2626"
                      : progress > 60
                      ? "#f59e0b"
                      : color || "#10b981",
                },
              }}
            />
          )}
          {action}
        </>
      )}
    </CardContent>
  </Card>
);

/** Financial Card Component */
export const FinancialCard = ({
  title,
  amount,
  count,
  type,
  icon,
  color,
  onClick,
  clickable,
  loading,
}) => (
  <Card
    sx={{
      borderRadius: 3,
      bgcolor: "white",
      border: "1px solid #e5e7eb",
      cursor: clickable ? "pointer" : "default",
      transition: "all 0.2s ease-in-out",
      "&:hover": clickable
        ? {
            transform: "translateY(-2px)",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            borderColor: color,
          }
        : {},
    }}
    onClick={clickable ? onClick : undefined}
  >
    <CardContent>
      {loading ? (
        <Box
          sx={{
            minHeight: 80,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography
              variant="caption"
              sx={{
                color: "#6b7280",
                fontWeight: 500,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, color: "#1a1a1a", my: 1 }}
            >
              {amount}
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280" }}>
              {count} {type}
            </Typography>
          </Box>
          <Avatar
            sx={{
              bgcolor: `${color}15`,
              color: color,
              width: 48,
              height: 48,
              fontSize: "1.5rem",
            }}
          >
            {icon}
          </Avatar>
        </Box>
      )}
    </CardContent>
  </Card>
);